(() => {
  const SELECTOR = ".like-share, [data-seasonal-trends-auth], [data-trending-article-favorite], [data-nail-academy], .section-college-article-template"

  function safeJsonParse (value, fallback) {
    try {
      return JSON.parse(value)
    } catch {
      return fallback
    }
  }

  function getState (root) {
    const key = root.dataset.storageKey || "like-share"
    const stored = safeJsonParse(window.localStorage.getItem(key), null)
    if (stored && typeof stored === "object") return { key, stored }

    const initial = {
      liked: root.dataset.initialLiked === "true",
      favorited: root.dataset.initialFavorited === "true",
      likeCount: Number(root.dataset.initialLikeCount || 0),
    }
    return { key, stored: initial }
  }

  function setState (key, next) {
    try {
      window.localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // ignore storage quota / privacy mode failures
    }
  }

  function setPressed (btn, pressed) {
    btn.setAttribute("aria-pressed", pressed ? "true" : "false")
    btn.classList.toggle("is-active", !!pressed)
  }

  function setExpanded (btn, expanded) {
    btn.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  function showToast (panel, text) {
    const toast = panel.querySelector(".like-share__toast")
    if (!toast) return
    if (text) toast.textContent = text
    toast.hidden = false
    window.clearTimeout(toast.__hideTimer)
    toast.__hideTimer = window.setTimeout(() => {
      toast.hidden = true
    }, 1400)
  }

  async function copyToClipboard (text) {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)

    const el = document.createElement("textarea")
    el.value = text
    el.setAttribute("readonly", "")
    el.style.position = "fixed"
    el.style.top = "-1000px"
    document.body.appendChild(el)
    el.select()
    document.execCommand("copy")
    document.body.removeChild(el)
  }

  /** 与点击同步打开新标签，避免浏览器拦截弹窗；部分平台仅提供官网/上传页入口。 */
  function getShareOpenUrl (platform, pageUrl) {
    const u = encodeURIComponent(pageUrl)
    switch (platform) {
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${u}`
      case "x":
        return `https://twitter.com/intent/tweet?url=${u}`
      case "pinterest":
        return `https://www.pinterest.com/pin/create/button/?url=${u}`
      case "tumblr":
        return `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${u}&posttype=link`
      case "instagram":
        return "https://www.instagram.com/"
      case "tiktok":
        return "https://www.tiktok.com/upload"
      case "youtube":
        return "https://www.youtube.com/upload"
      case "snapchat":
        return "https://web.snapchat.com/"
      case "vimeo":
        return "https://vimeo.com/upload"
      default:
        return pageUrl
    }
  }

  function clearDynamicReturnedLoginErrors (loginForm) {
    if (!(loginForm instanceof HTMLElement)) return
    loginForm.querySelectorAll("[data-like-share-auth-dynamic-error]").forEach((node) => {
      const wrap = node.closest(".like-share-auth-modal__field")
      wrap?.classList.remove("like-share-auth-modal__field--error")
      const inp = wrap?.querySelector(".like-share-auth-modal__input")
      inp?.removeAttribute("aria-invalid")
      inp?.removeAttribute("aria-describedby")
      node.remove()
    })
  }

  function injectReturnedFieldError (input, messageText) {
    const msg = String(messageText ?? "").trim()
    if (!(input instanceof HTMLInputElement) || !msg) return
    const wrap = input.closest(".like-share-auth-modal__field")
    if (!wrap) return
    const id = `${input.id}-like-share-return`
    wrap.classList.add("like-share-auth-modal__field--error")
    const p = document.createElement("p")
    p.id = id
    p.className = "like-share-auth-modal__field-error"
    p.setAttribute("role", "alert")
    p.dataset.likeShareAuthDynamicError = "1"
    p.textContent = msg
    input.insertAdjacentElement("afterend", p)
    input.setAttribute("aria-invalid", "true")
    input.setAttribute("aria-describedby", id)
  }

  function focusFirstInvalidLoginField (loginForm) {
    if (!(loginForm instanceof HTMLElement)) return
    const invalid = loginForm.querySelector('input[aria-invalid="true"]')
    if (invalid instanceof HTMLElement) invalid.focus()
  }

  const LOGIN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  function storefrontLoginConfig () {
    const api = window.globalFn?.storefrontApi
    if (api?.endpoint && api?.token) return { endpoint: api.endpoint, token: api.token }
    const root = window.Shopify?.routes?.root || "/"
    return {
      endpoint: `${root}api/2025-04/graphql.json`,
      token: "68fc996c788d6ff0a40aea26f65267d3",
    }
  }

  function setLoginSubmitLoading (submit, busy) {
    if (!(submit instanceof HTMLButtonElement)) return
    submit.classList.toggle("is-loading", !!busy)
    submit.disabled = !!busy
    if (busy) submit.setAttribute("aria-busy", "true")
    else submit.removeAttribute("aria-busy")
  }

  // Session errors must never navigate away from the active modal.
  async function submitCustomerLoginForm (loginForm) {
    const action = new URL(loginForm.action, window.location.href)
    if (action.origin !== window.location.origin) throw new Error("Unable to sign in. Please try again.")
    const captcha = window.Shopify?.captcha
    if (typeof captcha?.protect !== "function") {
      throw new Error("Verification is unavailable. Please refresh the page and try again.")
    }

    // protect() only binds CAPTCHA. Shopify wraps form.submit() to execute
    // verification, then calls the submit function that existed at bind time.
    // Use a fresh form so already-bound modal forms cannot capture a native
    // navigation, and each retry (including post-registration login) gets a token.
    const sessionForm = document.createElement("form")
    sessionForm.action = action.href
    sessionForm.method = "post"
    sessionForm.hidden = true
    for (const [name, value] of new FormData(loginForm)) {
      if (["h-captcha-response", "g-recaptcha-response", "recaptcha-v3-token", "form_key"].includes(name)) continue
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      sessionForm.append(input)
    }

    let body
    try {
      body = await new Promise((resolve, reject) => {
        let settled = false
        const finish = (error, payload) => {
          if (settled) return
          settled = true
          window.clearTimeout(timer)
          if (error) reject(error)
          else resolve(payload)
        }
        const timer = window.setTimeout(() => finish(new Error("Verification timed out. Please try again.")), 60000)
        sessionForm.submit = () => {
          if (settled) return
          try {
            const payload = new URLSearchParams(new FormData(sessionForm))
            if (!payload.get("h-captcha-response")?.trim()) {
              finish(new Error("Security verification failed. Please try again."))
              return
            }
            finish(null, payload)
          } catch (error) { finish(error) }
        }
        document.body.append(sessionForm)
        try {
          captcha.protect(sessionForm, () => {
            if (settled) return
            try { sessionForm.submit() } catch (error) { finish(error) }
          })
        } catch (error) { finish(error) }
      })
    } finally {
      sessionForm.replaceChildren()
      sessionForm.remove()
    }
    body.set("return_to", window.location.pathname + window.location.search)
    const response = await fetch(action.href, {
      method: "POST", credentials: "same-origin", redirect: "follow", cache: "no-store", body,
    })
    const result = new DOMParser().parseFromString(await response.text(), "text/html")
    const errors = safeJsonParse(result.getElementById("LikeShare-login-error-json")?.textContent || "", {}) || {}
    const message = errors.email || errors.password || errors.form
    if (message) throw new Error(String(message))
    if (response.ok && result.querySelector('meta[name="beetles-customer-authenticated"]')?.content === "true") return
    if (response.status === 429) throw new Error("Too many attempts. Please wait a moment and try again.")
    if (response.status === 400) throw new Error("Security verification failed. Please try again.")
    throw new Error("Unable to sign in. Please try again.")
  }

  async function storefrontCustomerAccessTokenCreate (email, password) {
    const { endpoint, token } = storefrontLoginConfig()
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query: `
          mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
            customerAccessTokenCreate(input: $input) {
              customerAccessToken { accessToken }
              customerUserErrors { field message }
            }
          }
        `,
        variables: { input: { email, password } },
      }),
    })
    return res.json()
  }

  async function storefrontCustomerCreate ({
    email,
    password,
    firstName,
    lastName,
    acceptsMarketing,
  }) {
    const { endpoint, token } = storefrontLoginConfig()
    const input = { email, password }
    if (firstName) input.firstName = firstName
    if (lastName) input.lastName = lastName
    if (typeof acceptsMarketing === "boolean") input.acceptsMarketing = acceptsMarketing
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query: `
          mutation customerCreate($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
              customer { id }
              customerUserErrors { field message code }
            }
          }
        `,
        variables: { input },
      }),
    })
    return res.json()
  }

  function applyFirstCustomerUserErrorToRegisterForm (registerForm, userErrs) {
    if (!Array.isArray(userErrs) || !userErrs.length) return
    const err = userErrs[0]
    const code = err?.code != null ? String(err.code).toUpperCase() : ""
    let msg = err?.message ? String(err.message) : ""
    if (!msg) return

    const emailIn = registerForm.querySelector('input[name="customer[email]"]')
    const passIn = registerForm.querySelector('input[name="customer[password]"]')

    // 邮箱在店铺里已存在但未激活（例如曾用邀请/未完成激活）。不是主题「新注册多发了确认邮件」。
    if (code === "CUSTOMER_DISABLED" || /verify your email address/i.test(msg)) {
      msg =
        "This email is already registered but not activated. Check your inbox or spam for an activation link from an earlier signup, or ask the store to enable your account in Admin → Customers. Then use Log in."
      injectReturnedFieldError(emailIn, msg)
      return
    }

    const fields = Array.isArray(err.field) ? err.field.map((f) => String(f).toLowerCase()) : []
    const key = fields.join(" ")
    if (key.includes("email")) injectReturnedFieldError(emailIn, msg)
    else if (key.includes("password")) injectReturnedFieldError(passIn, msg)
    else injectReturnedFieldError(passIn, msg)
  }

  function syncPasswordToggle (fieldWrap, visible) {
    const input = fieldWrap.querySelector("input")
    const btn = fieldWrap.querySelector("[data-like-share-auth-pw-toggle]")
    if (!input || !btn) return
    input.type = visible ? "text" : "password"
    fieldWrap.classList.toggle("is-password-visible", !!visible)
    const showLbl = btn.dataset.labelShow || btn.getAttribute("data-label-show") || "Show password"
    const hideLbl = btn.dataset.labelHide || btn.getAttribute("data-label-hide") || "Hide password"
    btn.setAttribute("aria-label", visible ? hideLbl : showLbl)
  }

  function initAuthModal (root) {
    const modal = root.querySelector("[data-like-share-auth-modal]")
    if (!modal) return
    if (root.dataset.likeShareAuthReady === "true") return
    root.dataset.likeShareAuthReady = "true"

    const dialog = modal.querySelector(".like-share-auth-modal__dialog")
    const tabButtons = modal.querySelectorAll("[data-like-share-auth-tab]")
    const panels = modal.querySelectorAll("[data-like-share-auth-panel]")
    const closeEls = modal.querySelectorAll("[data-like-share-auth-close]")

    let lastFocus = null

    function setTab (which) {
      window.BeetlesEmailAutocomplete?.close(modal)
      tabButtons.forEach((btn) => {
        const active = btn.getAttribute("data-like-share-auth-tab") === which
        btn.classList.toggle("is-active", active)
        btn.setAttribute("aria-selected", active ? "true" : "false")
      })
      panels.forEach((panel) => {
        const active = panel.getAttribute("data-like-share-auth-panel") === which
        panel.classList.toggle("is-active", active)
        panel.hidden = !active
      })
    }

    function openModal () {
      lastFocus = document.activeElement
      modal.hidden = false
      modal.setAttribute("aria-hidden", "false")
      document.documentElement.classList.add("like-share-auth-modal-open")
      const activePanel = modal.querySelector("[data-like-share-auth-panel].is-active")
      const email = activePanel?.querySelector('input[type="email"]')
      window.setTimeout(() => {
        (email || dialog)?.focus?.()
      }, 10)
    }

    function closeModal () {
      window.BeetlesEmailAutocomplete?.close(modal)
      modal.hidden = true
      modal.setAttribute("aria-hidden", "true")
      document.documentElement.classList.remove("like-share-auth-modal-open")
      if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus()
      lastFocus = null
    }

    tabButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const which = btn.getAttribute("data-like-share-auth-tab")
        if (which) setTab(which)
      })
    })

    closeEls.forEach((el) => {
      el.addEventListener("click", closeModal)
    })

    modal.addEventListener("click", (e) => {
      const target = e.target instanceof Element ? e.target : null
      if (target === modal || target?.closest(".like-share-auth-modal__backdrop")) closeModal()
    })

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || modal.hidden) return
      closeModal()
    })

    modal.addEventListener("click", (e) => {
      const btn = e.target instanceof Element ? e.target.closest("[data-like-share-auth-pw-toggle]") : null
      if (!(btn instanceof HTMLButtonElement)) return
      const wrap = btn.closest(".like-share-auth-modal__field--password")
      if (!wrap) return
      e.preventDefault()
      const visible = !wrap.classList.contains("is-password-visible")
      syncPasswordToggle(wrap, visible)
    })

    const loginForm = modal.querySelector(".like-share-auth-modal__form--login")
    let loginSubmitInFlight = false

    if (loginForm instanceof HTMLFormElement) {
      loginForm.addEventListener("submit", (e) => {
        e.preventDefault()
        // Shopify CAPTCHA's bubbling listener schedules a native submit even
        // when defaultPrevented is true. Own this event in the capture phase;
        // session creation still uses the protected asynchronous path below.
        e.stopImmediatePropagation()
        if (loginSubmitInFlight) return

        const submit = loginForm.querySelector('.like-share-auth-modal__submit[type="submit"]')
        if (!(submit instanceof HTMLButtonElement)) return

        clearDynamicReturnedLoginErrors(loginForm)

        const emailIn = loginForm.querySelector('input[name="customer[email]"]')
        const passIn = loginForm.querySelector('input[name="customer[password]"]')
        const email = emailIn?.value?.trim() || ""
        const password = passIn?.value || ""

        function surfaceError (input, message) {
          injectReturnedFieldError(input, message)
          focusFirstInvalidLoginField(loginForm)
        }

        if (!email) {
          surfaceError(emailIn, "Email cannot be empty")
          return
        }
        if (!password) {
          surfaceError(passIn, "Password cannot be empty")
          return
        }
        if (!LOGIN_EMAIL_RE.test(email)) {
          surfaceError(emailIn, "Please enter a valid email address")
          return
        }

        loginSubmitInFlight = true
        setLoginSubmitLoading(submit, true)

        storefrontCustomerAccessTokenCreate(email, password)
          .then(async (data) => {
            if (data?.errors?.length) {
              injectReturnedFieldError(
                passIn,
                data.errors[0]?.message || "Something went wrong. Try again.",
              )
              focusFirstInvalidLoginField(loginForm)
              return
            }

            const block = data?.data?.customerAccessTokenCreate
            const accessToken = block?.customerAccessToken?.accessToken
            const userErrs = block?.customerUserErrors

            if (!accessToken) {
              const msg =
                Array.isArray(userErrs) && userErrs.length && userErrs[0]?.message
                  ? String(userErrs[0].message)
                  : "Incorrect email or password"
              injectReturnedFieldError(passIn, msg)
              focusFirstInvalidLoginField(loginForm)
              return
            }

            await submitCustomerLoginForm(loginForm)
            // Keep the Storefront customer token for authenticated profile edits.
            // Passwords are never stored; the profile flow verifies the customer ID.
            try { window.sessionStorage.setItem('beetles-storefront-customer-token', accessToken) } catch {}
            window.location.reload()
          })
          .catch((error) => {
            injectReturnedFieldError(
              passIn,
              error?.message || "Network error. Check your connection and try again.",
            )
            focusFirstInvalidLoginField(loginForm)
          })
          .finally(() => {
            loginSubmitInFlight = false
            setLoginSubmitLoading(submit, false)
          })
      }, { capture: true })
    }

    const registerForm = modal.querySelector(".like-share-auth-modal__form--register")
    let registerSubmitInFlight = false
    let registeredEmail = null
    let successfulRegistration = null

    if (registerForm instanceof HTMLFormElement && loginForm instanceof HTMLFormElement) {
      registerForm.addEventListener("submit", (e) => {
        e.preventDefault()
        // Do not let CAPTCHA submit /account after customerCreate returns errors.
        e.stopImmediatePropagation()
        if (registerSubmitInFlight) return

        const submit = registerForm.querySelector('.like-share-auth-modal__submit[type="submit"]')
        if (!(submit instanceof HTMLButtonElement)) return

        clearDynamicReturnedLoginErrors(registerForm)

        const emailIn = registerForm.querySelector('input[name="customer[email]"]')
        const passIn = registerForm.querySelector('input[name="customer[password]"]')
        const firstIn = registerForm.querySelector('input[name="customer[first_name]"]')
        const lastIn = registerForm.querySelector('input[name="customer[last_name]"]')

        const email = emailIn?.value?.trim() || ""
        const password = passIn?.value || ""
        const firstName = (firstIn?.value ?? "").trim()
        const lastName = (lastIn?.value ?? "").trim()
        // 固定 false：避免 Shopify 对营销订阅发「请确认邮箱」类邮件；账户仍通过密码即刻创建并可登录。
        // 若要在客户档案中记录营销同意，可改为 agreeIn instanceof HTMLInputElement && agreeIn.checked
        const acceptsMarketing = false

        function surfaceRegError (input, message) {
          injectReturnedFieldError(input, message)
          focusFirstInvalidLoginField(registerForm)
        }

        if (!email) {
          surfaceRegError(emailIn, "Email cannot be empty")
          return
        }
        if (!password) {
          surfaceRegError(passIn, "Password cannot be empty")
          return
        }
        if (!LOGIN_EMAIL_RE.test(email)) {
          surfaceRegError(emailIn, "Please enter a valid email address")
          return
        }

        registerSubmitInFlight = true
        setLoginSubmitLoading(submit, true)

        const registrationRequest = registeredEmail === email
          ? Promise.resolve(successfulRegistration)
          : storefrontCustomerCreate({
            email,
            password,
            firstName,
            lastName,
            acceptsMarketing,
          })
        registrationRequest
          .then(async (data) => {
            if (data?.errors?.length) {
              const gqlMsg = data.errors[0]?.message || "Something went wrong. Try again."
              injectReturnedFieldError(
                registerForm.querySelector('input[name="customer[email]"]'),
                gqlMsg,
              )
              focusFirstInvalidLoginField(registerForm)
              return
            }

            const createBlock = data?.data?.customerCreate
            const userErrs = createBlock?.customerUserErrors
            if (Array.isArray(userErrs) && userErrs.length) {
              applyFirstCustomerUserErrorToRegisterForm(registerForm, userErrs)
              focusFirstInvalidLoginField(registerForm)
              return
            }
            if (!createBlock?.customer?.id) {
              surfaceRegError(passIn, "Could not create account. Try again.")
              return
            }

            // Retry session failures without creating the same account again.
            registeredEmail = email
            successfulRegistration = data
            const loginEmailIn = loginForm.querySelector('input[name="customer[email]"]')
            const loginPassIn = loginForm.querySelector('input[name="customer[password]"]')
            if (loginEmailIn instanceof HTMLInputElement) loginEmailIn.value = email
            if (loginPassIn instanceof HTMLInputElement) loginPassIn.value = password

            const regAgree = registerForm.querySelector("[data-like-share-auth-agree]")
            const loginAgree = loginForm.querySelector("[data-like-share-auth-agree]")
            if (loginAgree instanceof HTMLInputElement && regAgree instanceof HTMLInputElement) {
              loginAgree.checked = regAgree.checked
            }

            await submitCustomerLoginForm(loginForm)
            // Registration also establishes the token needed for profile edits.
            try {
              const auth = await storefrontCustomerAccessTokenCreate(email, password)
              const customerToken = auth?.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken
              if (customerToken) window.sessionStorage.setItem('beetles-storefront-customer-token', customerToken)
            } catch {}
            window.location.reload()
          })
          .catch((error) => {
            const message = error?.message || "Network error. Check your connection and try again."
            surfaceRegError(passIn, registeredEmail === email
              ? "Your account was created, but sign-in failed. " + message
              : message)
          })
          .finally(() => {
            registerSubmitInFlight = false
            setLoginSubmitLoading(submit, false)
          })
      }, { capture: true })
    }

    root.__likeShareAuth = { openModal, closeModal, setTab }

    /** 登录失败后 return_to 回到当前页，Liquid 渲染错误；自动打开登录 tab 并把焦点给到首个无效字段 */
    if (modal.querySelector("[data-like-share-auth-login-error]")) {
      const run = () => {
        setTab("login")
        openModal()
        window.setTimeout(() => {
          const loginForm = modal.querySelector(".like-share-auth-modal__form--login")
          focusFirstInvalidLoginField(loginForm)
        }, 30)
      }
      window.requestAnimationFrame(run)
    }
  }

  function init (root) {
    const { key, stored } = getState(root)
    const pill = root.querySelector(".like-share__pill")
    initAuthModal(root)
    if (!pill) return

    const likeBtn = pill.querySelector(".like-share__slot--like")
    const likeCountEl = pill.querySelector(".like-share__count")
    const favBtn = pill.querySelector(".like-share__slot--favorite")
    const shareBtn = pill.querySelector(".like-share__slot--share")
    const panel = pill.querySelector(".like-share__panel")
    const panelClose = pill.querySelector(".like-share__panel-close")
    const copyBtn = pill.querySelector(".like-share__panel-link--copy")

    let state = {
      liked: !!stored.liked,
      favorited: !!stored.favorited,
      likeCount: Number.isFinite(stored.likeCount) ? stored.likeCount : 0,
    }

    if (likeBtn) {
      setPressed(likeBtn, state.liked)
      if (likeCountEl) likeCountEl.textContent = String(state.likeCount)
      likeBtn.addEventListener("click", () => {
        if (root.dataset.customerLoggedIn !== "true") {
          root.__likeShareAuth?.setTab?.("login")
          root.__likeShareAuth?.openModal?.()
          return
        }
        const nextLiked = !state.liked
        state = {
          ...state,
          liked: nextLiked,
          likeCount: Math.max(0, state.likeCount + (nextLiked ? 1 : -1)),
        }
        setPressed(likeBtn, state.liked)
        if (likeCountEl) likeCountEl.textContent = String(state.likeCount)
        setState(key, state)
      })
    }

    if (favBtn) {
      setPressed(favBtn, state.favorited)
      favBtn.addEventListener("click", () => {
        state = { ...state, favorited: !state.favorited }
        setPressed(favBtn, state.favorited)
        setState(key, state)
      })
    }

    function closePanel () {
      if (!panel || !shareBtn) return
      panel.hidden = true
      setExpanded(shareBtn, false)
    }

    function openPanel () {
      if (!panel || !shareBtn) return
      panel.hidden = false
      setExpanded(shareBtn, true)
    }

    if (shareBtn && panel) {
      setExpanded(shareBtn, false)
      panel.hidden = true

      shareBtn.addEventListener("click", () => {
        const willOpen = shareBtn.getAttribute("aria-expanded") !== "true"
        if (willOpen) openPanel()
        else closePanel()
      })

      panelClose?.addEventListener("click", closePanel)

      document.addEventListener("click", (e) => {
        if (panel.hidden) return
        const t = e.target
        if (!(t instanceof Element)) return
        if (root.contains(t)) return
        closePanel()
      })

      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return
        if (document.documentElement.classList.contains("like-share-auth-modal-open")) return
        closePanel()
      })
    }

    if (panel && copyBtn) {
      copyBtn.addEventListener("click", async () => {
        const url = root.dataset.shareUrl || window.location.href
        try {
          await copyToClipboard(url)
          showToast(panel, root.dataset.copiedToast || "Link copied")
        } catch {
          showToast(panel, "Copy failed")
        }
      })
    }

    if (panel) {
      panel.addEventListener("click", async (e) => {
        const t = e.target
        const btn =
          t instanceof Element ? t.closest("[data-share-platform]") : null
        if (!(btn instanceof HTMLButtonElement) || !btn.dataset.sharePlatform)
          return

        const pageUrl =
          root.dataset.shareUrl?.trim() || window.location.href
        const openUrl = getShareOpenUrl(btn.dataset.sharePlatform, pageUrl)

        window.open(openUrl, "_blank", "noopener,noreferrer")

        try {
          await copyToClipboard(pageUrl)
          showToast(panel, root.dataset.copiedToast || "Link copied")
        } catch {
          showToast(panel, "Copy failed")
        }
      })
    }
  }

  function boot () {
    document.querySelectorAll(SELECTOR).forEach((el) => init(el))
    document.querySelectorAll("[data-header-auth-host]").forEach((el) => initAuthModal(el))
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot)
  } else {
    boot()
  }
})();

